import {useCallback, useEffect, useRef, useState} from 'react';
import {useDebounce} from 'react-use';
import {Stepper} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';

import {useEventBarV2} from '../EventBarV2Provider';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';

type GroupSizeSelectorProps = {
  idSuffix: string;
};

const GroupSizeSelector: React.FC<GroupSizeSelectorProps> = ({idSuffix}) => {
  const {t} = useTranslation('app-bar');
  const {headCount, setHeadCount} = useEventBarV2();
  const {trackHeadcount} = useEventBarV2Tracking();

  // Debounce so rapid stepper +/- (or typing) collapses into a single settled headcount-updated.
  const [pendingHeadcount, setPendingHeadcount] = useState<number | null>(null);
  // Latest pending value, read by the stable `flush` below (kept off `flush`'s deps so it stays
  // stable and the unmount cleanup fires only on unmount). Last-tracked value dedupes the debounce
  // fire against the unmount flush so they never double-report.
  const pendingHeadcountRef = useRef<number | null>(null);
  const trackedHeadcountRef = useRef<number | null>(null);

  const flush = useCallback(() => {
    const value = pendingHeadcountRef.current;
    if (value != null && value !== trackedHeadcountRef.current) {
      trackedHeadcountRef.current = value;
      trackHeadcount(value);
    }
  }, [trackHeadcount]);

  const commit = useCallback(
    (value: number | undefined) => {
      setHeadCount(value);
      pendingHeadcountRef.current = value ?? null;
      setPendingHeadcount(value ?? null);
    },
    [setHeadCount],
  );

  useDebounce(flush, 500, [pendingHeadcount]);

  // Flush on unmount so the headcount-updated event isn't lost.
  useEffect(() => () => flush(), [flush]);

  const labelId = `group-size-selector-label-${idSuffix}`;

  const isEmpty = headCount == null;

  // Arrow keys on the input would otherwise step from blank to the 0 minimum, so mirror the
  // empty-field rules here: ArrowUp from blank goes to 1 and ArrowDown from blank is a no-op.
  // Capture + stopPropagation pre-empts Base UI's own key handling on the input.
  const handleKeyDownCapture = (event: React.KeyboardEvent) => {
    if (!isEmpty) return;
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      commit(1);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <div className="flex w-full flex-col">
      <span className="text-sm font-semibold text-peppercorn-800" id={labelId}>
        {t('eventBarV2.who.guestCount.label')}
      </span>
      <span className="text-sm text-peppercorn-500">{t('eventBarV2.who.guestCount.subtitle')}</span>
      <Stepper.Root
        /* TODO: this is code smell to adjust component styles for event bar release, but they should be adjusted in Tapas component and removed here */
        className="self-center pt-2 text-2xl [&>div]:h-10 [&_button:disabled]:border-peppercorn-200 [&_button:disabled_svg]:text-peppercorn-300 [&_button]:h-8 [&_button]:w-8 [&_button]:p-3 [&_input]:h-10 [&_input]:min-w-16 [&_input]:max-w-24 [&_input]:text-center [&_input]:font-bold"
        max={9999}
        // 0 is allowed so a typed 0 reaches `onValueChange` and can be treated as blank rather than
        // clamped up to 1.
        min={0}
        step={1}
        // Base UI models an empty field as `null`, so blank needs no sentinel of its own.
        value={headCount ?? null}
        onValueChange={(value, eventDetails) => {
          // Pressing "+" on a blank field lands on the 0 minimum; the intent is 1.
          if (value === 0 && eventDetails.reason === 'increment-press') {
            commit(1);
            return;
          }
          // Both an explicit 0 and an emptied field mean "blank".
          commit(value != null && value > 0 ? value : undefined);
        }}
      >
        <Stepper.Group aria-labelledby={labelId} className="justify-center">
          {/* Base UI only auto-disables "-" once the value is at `min`, and blank is `null` rather
              than 0, so disable it here instead. This replaces reaching into the rendered button
              via a ref — and Base UI already keeps the +/- buttons out of the tab order. */}
          <Stepper.Decrement disabled={isEmpty} />
          <Stepper.Input
            aria-labelledby={labelId}
            id={`group-size-${idSuffix}`}
            onKeyDownCapture={handleKeyDownCapture}
          />
          <Stepper.Increment />
        </Stepper.Group>
      </Stepper.Root>
    </div>
  );
};

export default GroupSizeSelector;
